/**
 * @fileoverview UIの描画と操作を専門に担当します。
 * DOM要素の取得、表示/非表示の切り替え、動的なHTMLの生成など、ユーザーインターフェースに関する処理を一元管理します。
 */
import { Chart, TooltipItem } from "chart.js/auto";

import { App } from "./app";
import { Constants } from "./constants";
import State from "./state";
import { UpdateQuestionRequest } from "./types/edit-question.d";
import { Area, ExamData } from "./types/exam.d";
import { Question, Option } from "./types/question.d";
import type { UIElements } from "./types/ui-elements.d";
import type { UIManager } from "./types/ui-manager.d";

/**
 * UI関連の処理をまとめたシングルトンオブジェクト。
 * @implements {UIManager}
 */
export const UI: UIManager = {
  /**
   * @property {Record<string, HTMLElement>} elements - 頻繁にアクセスするDOM要素をキャッシュするためのオブジェクト。
   *           キーはDOM要素のIDをキャメルケースに変換したもの、値は対応するHTMLElementです。
   */
  elements: {} as UIElements,

  /**
   * @property {Record<string, ExamData>} _examDataCache - 各試験のデータを一時的に保持するためのプライベートキャッシュ。
   *           キーは "vendor||examName" 形式の文字列、値は試験データオブジェクトです。
   *           アコーディオンUIの構築時に使用され、パネル開閉時のデータ参照を高速化します。
   */
  _examDataCache: {},

  /**
   * アプリケーション全体のイベントリスナーを設定します。
   * 主にUI要素のインタラクション（設定メニューの開閉など）を処理します。
   */
  setupEventListeners() {
    // --- 設定ボタンのイベントリスナー ---
    // ボタンをクリックすると、設定メニューの表示/非表示が切り替わります。
    this.elements.settingsButton.addEventListener("click", () => {
      const settingsMenu = this.elements.settingsMenu;
      if (settingsMenu) {
        // 現在の表示状態を確認
        const isHidden = settingsMenu.classList.contains(
          Constants.CSS_CLASSES.HIDDEN
        );
        // isHiddenがtrueなら表示、falseなら非表示にする
        this.toggleElementVisibility(settingsMenu, isHidden);
      }
    });

    // --- ドキュメント全体のクリックイベントリスナー ---
    // メニューが開いているときにその外側をクリックすると、メニューを閉じます。
    document.addEventListener("click", (event: MouseEvent) => {
      const settingsMenu = this.elements.settingsMenu;
      // メニューが表示されているかを確認
      if (
        settingsMenu &&
        !settingsMenu.classList.contains(Constants.CSS_CLASSES.HIDDEN)
      ) {
        const target = event.target as HTMLElement;
        // クリックされた要素が設定ボタン自身でも、メニュー内部の要素でもないことを確認
        const isClickOutside =
          !this.elements.settingsButton.contains(target) &&
          !settingsMenu.contains(target);
        // 条件を満たす場合（メニュー外のクリック）のみメニューを閉じる
        if (isClickOutside) {
          this.toggleElementVisibility(settingsMenu, false);
        }
      }
    });
  },

  /**
   * アプリケーションで使用する主要なDOM要素を一度だけ取得し、`UI.elements`にキャッシュします。
   * これにより、DOMへのアクセス回数を減らし、パフォーマンスを向上させます。
   */
  cacheDomElements() {
    // `Constants.ELEMENT_IDS`に定義されたすべてのIDをループ処理
    for (const key in Constants.ELEMENT_IDS) {
      const id = (Constants.ELEMENT_IDS as Record<string, string>)[key];
      const camelCaseKey = id.replace(/-([a-z0-9])/g, (g) =>
        g[1].toUpperCase()
      );
      const element = document.getElementById(id);

      if (element) {
        // UIElements インターフェースの型に基づいて、より具体的な型にキャスト
        if (key.includes("Btn")) {
          (this.elements as UIElements)[camelCaseKey as keyof UIElements] =
            element as unknown as HTMLButtonElement;
        } else if (key.includes("Select")) {
          (this.elements as UIElements)[camelCaseKey as keyof UIElements] =
            element as unknown as HTMLSelectElement;
        } else if (key.includes("Chart")) {
          (this.elements as UIElements)[camelCaseKey as keyof UIElements] =
            element as unknown as HTMLCanvasElement;
        } else {
          (this.elements as UIElements)[camelCaseKey as keyof UIElements] =
            element as HTMLElement;
        }
      } else {
        console.warn(
          `DOM要素が見つかりません: ID '${id}' (キー: '${camelCaseKey}')`
        );
        (this.elements as UIElements)[camelCaseKey as keyof UIElements] =
          undefined;
      }
    }

    // 特定の要素の子要素も頻繁に利用するため、ここでキャッシュしておく
    // これらの要素は、親要素が null でないことを前提とする
    if (this.elements.feedback) {
      this.elements.feedbackIcon = this.elements.feedback.querySelector(
        ".feedback-icon"
      ) as HTMLElement;
      this.elements.feedbackMessage = this.elements.feedback.querySelector(
        ".feedback-message"
      ) as HTMLElement;
    }

    // 初期状態では問題編集メニューを非表示にする
    if (this.elements.editQuestionMenuItem) {
      this.toggleElementVisibility(this.elements.editQuestionMenuItem, false);
    }
  },

  toggleElementVisibility(element: HTMLElement, show: boolean) {
    if (element) {
      element.classList.toggle(Constants.CSS_CLASSES.HIDDEN, !show);
    }
  },

  /**
   * ローディングインジケーターの表示/非表示を切り替えます。
   * @param {boolean} show - `true`でローディングを表示、`false`で非表示にします。
   */
  showLoading(show: boolean) {
    this.toggleElementVisibility(this.elements.loadingIndicator, show);
    if (show) {
      // ローディング中は他の主要エリアを非表示にする
      this.toggleElementVisibility(this.elements.examSelectionArea, false);
      this.toggleElementVisibility(this.elements.quizArea, false);
      this.toggleElementVisibility(this.elements.resultArea, false);
    }
  },

  /**
   * 指定されたアプリケーションの主要エリアを表示し、他のエリアを非表示にします。
   * エリア切り替え時には、フェードインアニメーションを適用し、ページを最上部にスクロールします。
   * @param {string} areaName - 表示するエリアの名前（'examSelection', 'quiz', 'result'）。
   */
  showArea(areaName: string) {
    // エリア切り替え時には、まずローディング表示を確実に非表示にする
    this.showLoading(false);
    // 問題編集メニューは一旦非表示にする（解説画面でのみ個別に表示制御するため）
    this.toggleElementVisibility(this.elements.editQuestionMenuItem, false);

    // アプリケーションの主要な表示エリアをオブジェクトにマッピング
    const areas: Record<string, HTMLElement> = {
      examSelection: this.elements.examSelectionArea,
      quiz: this.elements.quizArea,
      result: this.elements.resultArea,
    };

    // すべてのエリアをループし、指定されたエリアのみを表示、他は非表示にする
    for (const name in areas) {
      const element = areas[name];
      if (element) {
        const show = name === areaName;
        this.toggleElementVisibility(element, show);

        // 表示対象のエリアだった場合、フェードインアニメーションを適用
        if (show) {
          // アニメーションクラスを一度削除
          element.classList.remove(Constants.CSS_CLASSES.FADE_IN);
          // DOMの再描画を強制（リフロー）させることで、再度アニメーションが実行されるようにする
          void element.offsetWidth;
          // アニメーションクラスを再度追加
          element.classList.add(
            Constants.CSS_CLASSES.ANIMATED,
            Constants.CSS_CLASSES.FADE_IN
          );
          // アニメーションが完了したら、不要になったクラスを削除する
          element.addEventListener(
            "animationend",
            () =>
              element.classList.remove(
                Constants.CSS_CLASSES.ANIMATED,
                Constants.CSS_CLASSES.FADE_IN
              ),
            { once: true } // イベントリスナーを一度きりにする
          );
        }
      }
    }
    // 新しいエリアのコンテンツが画面上部に表示されるように、ウィンドウをスクロール
    window.scrollTo(0, 0);
  },

  /**
   * カスタム確認ダイアログ（OK/キャンセル）を表示します。
   * @param {string} title - ダイアログのタイトル。
   * @param {string} message - ダイアログに表示するメッセージ。
   * @param {() => void} okCallback - OKボタンクリック時に実行されるコールバック。
   * @param {(() => void) | null} [cancelCallback=null] - キャンセルボタンクリック時に実行されるコールバック。
   */
  showCustomConfirm(
    title: string,
    message: string,
    okCallback: () => void,
    cancelCallback: (() => void) | null = null
  ) {
    if (!this.elements.customConfirmDialog) {
      return;
    }

    this.elements.customConfirmTitle.textContent = title;
    this.elements.customDialogBody.innerHTML = `<p class="custom-dialog-message">${message}</p>`;
    this.elements.customConfirmOkBtn.textContent = "OK";
    this.toggleElementVisibility(this.elements.customConfirmCancelBtn, true);

    // コールバックをStateに一時保存

    State._currentDialogOkCallback = okCallback;

    State._currentDialogCancelCallback = cancelCallback;

    this.toggleElementVisibility(this.elements.customConfirmDialog, true);
  },

  /**
   * カスタム情報ダイアログ（OKのみ）を表示します。
   * @param {string} title - ダイアログのタイトル。
   * @param {string} message - ダイアログに表示するメッセージ。
   * @param {(() => void) | null} [okCallback=null] - OKボタンクリック時に実行されるコールバック。
   */
  showCustomInfoDialog(
    title: string,
    message: string,
    okCallback: (() => void) | null = null
  ) {
    this.elements.customConfirmTitle.textContent = title;
    this.elements.customDialogBody.innerHTML = `<p class="custom-dialog-message">${message}</p>`;
    this.elements.customConfirmOkBtn.textContent = "OK";
    this.toggleElementVisibility(this.elements.customConfirmCancelBtn, false);

    State._currentDialogOkCallback = okCallback;
    State._currentDialogCancelCallback = null;

    this.toggleElementVisibility(this.elements.customConfirmDialog, true);
  },

  showAreaSelectionDialog(
    title: string,
    availableAreas: Area[],
    currentSelection: number[],
    okCallback: (selected: number[]) => void,
    cancelCallback: (() => void) | null = null
  ) {
    this.elements.customConfirmTitle.textContent = title;
    const dialogBody = this.elements.customDialogBody;
    dialogBody.innerHTML = ""; // Clear previous content

    const list = document.createElement("div");
    list.className = "area-selection-list";

    // "All Areas" checkbox
    const allLabel = document.createElement("label");
    const allCheckbox = document.createElement("input");
    allCheckbox.type = "checkbox";
    allCheckbox.value = "_all"; // Special value
    allLabel.appendChild(allCheckbox);
    allLabel.append("すべての領域");
    list.appendChild(allLabel);

    const areaCheckboxes: HTMLInputElement[] = [];
    availableAreas.forEach((area) => {
      const label = document.createElement("label");
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.value = area.id.toString();
      // Default to all selected
      checkbox.checked =
        currentSelection.length === 0 || currentSelection.includes(area.id);
      label.appendChild(checkbox);
      label.append(area.name);
      list.appendChild(label);
      areaCheckboxes.push(checkbox);
    });

    allCheckbox.checked = areaCheckboxes.every((cb) => cb.checked);

    // Event handling for checkboxes
    allCheckbox.addEventListener("change", () => {
      areaCheckboxes.forEach((cb) => (cb.checked = allCheckbox.checked));
    });
    areaCheckboxes.forEach((cb) => {
      cb.addEventListener("change", () => {
        allCheckbox.checked = areaCheckboxes.every((cb) => cb.checked);
      });
    });

    dialogBody.appendChild(list);

    this.elements.customConfirmOkBtn.textContent = "OK";
    this.toggleElementVisibility(this.elements.customConfirmCancelBtn, true);

    State._currentDialogOkCallback = () => {
      const selected = areaCheckboxes
        .filter((cb) => cb.checked)
        .map((cb) => parseInt(cb.value, 10));
      // If all are checked, treat as empty array (means "all")
      if (selected.length === availableAreas.length) {
        okCallback([]);
      } else {
        okCallback(selected);
      }
    };
    State._currentDialogCancelCallback = cancelCallback;

    this.toggleElementVisibility(this.elements.customConfirmDialog, true);
  },

  /**
   * 表示されているカスタムダイアログを非表示にし、関連するコールバックをリセットします。
   */
  hideCustomConfirm() {
    this.toggleElementVisibility(this.elements.customConfirmDialog, false);
    // wideクラスがあれば削除
    if (this.elements.customDialogContent) {
      this.elements.customDialogContent.classList.remove("wide");
    }
    // コールバックをリセット
    State._currentDialogOkCallback = null;
    State._currentDialogCancelCallback = null;
  },

  /**
   * 問題数選択のUI要素（ラジオボタンとドロップダウン）を動的に生成します。
   */
  populateQuizLengthOptions() {
    // 既存の選択肢をクリア
    this.elements.quizLengthOptionsContainer.innerHTML = "";
    this.elements.quizLengthSelect.innerHTML = "";

    // 設定ファイルから問題数の選択肢を取得してループ
    Constants.APP_CONFIG.QUIZ_LENGTH_OPTIONS.forEach((length: number) => {
      // 現在Stateで選択されている値と同じか判定
      const isChecked = length === State.selectedQuizLength;

      // --- ラジオボタン形式のUIを生成 (PC/タブレット向け) ---
      const label = document.createElement("label");
      const radio = document.createElement("input");
      radio.type = "radio";
      radio.name = "global_quiz_length"; // name属性を統一してグループ化
      radio.value = length.toString();
      radio.checked = isChecked;
      const span = document.createElement("span");
      span.textContent = `${length} 問`;
      label.appendChild(radio);
      label.appendChild(span);
      this.elements.quizLengthOptionsContainer.appendChild(label);

      // --- ドロップダウン形式のUIを生成 (スマートフォン向け) ---
      const option = document.createElement("option");
      option.value = length.toString();
      option.textContent = `${length} 問`;
      option.selected = isChecked;
      this.elements.quizLengthSelect.appendChild(option);
    });
  },

  /**
   * 問題抽出アルゴリズム選択のUI要素（ラジオボタンとドロップダウン）を動的に生成します。
   */
  populateQuizAlgorithmOptions() {
    const container = this.elements.quizAlgorithmOptionsContainer;
    const select = this.elements.quizAlgorithmSelect as HTMLSelectElement;
    // 必要なDOM要素が見つからない場合は処理を中断
    if (!container || !select) {
      console.error("Algorithm selection UI elements not found.");
      return;
    }

    // 既存の選択肢をクリア
    container.innerHTML = "";
    select.innerHTML = "";

    const algorithms = Constants.APP_CONFIG.QUIZ_ALGORITHMS;
    // アルゴリズムの内部値と表示名をマッピング
    const algorithmNames: Record<string, string> = {
      [algorithms.BALANCE]: "バランスモード",
      [algorithms.NEW_ONLY]: "新規モード",
      [algorithms.REVIEW]: "復習モード",
      [algorithms.INCORRECT_ONLY]: "誤答モード",
    };

    // 各アルゴリズムの選択肢を生成
    Object.values(algorithms).forEach((value: string) => {
      const displayName = algorithmNames[value] || value; // 表示名が定義されていなければ内部値をそのまま使用
      const isChecked = value === State.selectedQuizAlgorithm; // 現在選択されているアルゴリズムか判定

      // --- ラジオボタン形式のUIを生成 (PC/タブレット向け) ---
      const label = document.createElement("label");
      const radio = document.createElement("input");
      radio.type = "radio";
      radio.name = "quiz_algorithm"; // 同じname属性でグループ化
      radio.value = value;
      radio.checked = isChecked;
      const span = document.createElement("span");
      span.textContent = displayName;
      label.appendChild(radio);
      label.appendChild(span);
      container.appendChild(label);

      // --- ドロップダウン形式のUIを生成 (スマートフォン向け) ---
      const option = document.createElement("option");
      option.value = value;
      option.textContent = displayName;
      option.selected = isChecked;
      select.appendChild(option);
    });
  },

  /**
   * 出題モード選択のUI要素（ラジオボタンとドロップダウン）を動的に生成します。
   */
  populateQuizModeOptions() {
    const container = this.elements.quizModeOptionsContainer;
    const select = this.elements.quizModeSelect as HTMLSelectElement;
    // 必要なDOM要素が見つからない場合は処理を中断
    if (!container || !select) {
      console.error("Quiz mode selection UI elements not found.");
      return;
    }

    // 既存の選択肢をクリア
    container.innerHTML = "";
    select.innerHTML = "";

    const modes = Constants.APP_CONFIG.QUIZ_MODES;
    // 出題モードの内部値と表示名をマッピング
    const modeNames: Record<string, string> = {
      [modes.ADAPTIVE]: "アダプティブモード",
      [modes.NORMAL]: "通常モード",
      [modes.FLASHCARD]: "フラッシュカード",
    };

    // 各出題モードの選択肢を生成
    Object.values(modes).forEach((value: string) => {
      const displayName = modeNames[value] || value; // 表示名が定義されていなければ内部値をそのまま使用
      const isChecked = value === State.selectedQuizMode; // 現在選択されているモードか判定

      // --- ラジオボタン形式のUIを生成 (PC/タブレット向け) ---
      const label = document.createElement("label");
      const radio = document.createElement("input");
      radio.type = "radio";
      radio.name = "quiz_mode"; // 同じname属性でグループ化
      radio.value = value;
      radio.checked = isChecked;
      const span = document.createElement("span");
      span.textContent = displayName;
      label.appendChild(radio);
      label.appendChild(span);
      container.appendChild(label);

      // --- ドロップダウン形式のUIを生成 (スマートフォン向け) ---
      const option = document.createElement("option");
      option.value = value;
      option.textContent = displayName;
      option.selected = isChecked;
      select.appendChild(option);
    });
  },

  /**
   * ユーザーが未ログインの場合に、ログインを促すメッセージを表示します。
   */
  displayLoginPrompt() {
    // ログインエリアを表示し、他の主要なUIエリアを非表示にする
    this.toggleElementVisibility(this.elements.loginArea, true);
    this.toggleElementVisibility(this.elements.examSelectionArea, false);
    this.toggleElementVisibility(this.elements.quizArea, false);
    this.toggleElementVisibility(this.elements.resultArea, false);
  },

  /**
   * 取得した試験情報リストに基づいて、アコーディオン形式のUIを動的に構築します。
   * @param {ExamData[]} examDataList - 表示する試験データの配列。
   */
  buildExamAccordion(examDataList: ExamData[]) {
    const container = this.elements.examAccordionContainer;
    // アコーディオンを再構築する前に、既存の内容をすべてクリア
    container.innerHTML = "";
    // ログインプロンプトなどが表示されていた場合に備え、グローバルオプションを再表示
    this.toggleElementVisibility(
      document.getElementById("global-quiz-options") as HTMLElement,
      true
    );
    this.toggleElementVisibility(
      document.getElementById("quiz-algorithm-options") as HTMLElement,
      true
    );
    this.toggleElementVisibility(
      this.elements.examSelectionArea.querySelector(
        "h2.section-title"
      ) as HTMLElement,
      true
    );
    this._examDataCache = {}; // 試験データキャッシュをリセット

    // もし既に開いているアコーディオンパネルがあれば、それを閉じる
    if (State.activeAccordionPanel) {
      const activeHeader = State.activeAccordionPanel
        .previousElementSibling as HTMLElement;
      if (activeHeader) this.closeAccordionPanel(activeHeader);
    }

    // 表示する試験データがない場合はメッセージを表示して処理を終了
    if (!examDataList || examDataList.length === 0) {
      container.innerHTML = "<p>利用可能な試験がありません。</p>";
      return;
    }

    // DOM操作のパフォーマンス向上のため、DocumentFragmentを使用して要素をメモリ上に追加
    const fragment = document.createDocumentFragment();
    examDataList.forEach((exam, index) => {
      // 試験を一意に識別するためのキーを生成し、データをキャッシュ
      const examKey = `${exam.vendor || ""}||${exam.exam_name || ""}`;
      this._examDataCache[examKey] = exam;
      // 各アコーディオン要素に一意のIDを付与
      const uniqueId = `exam-accordion-${index}`;

      // アコーディオンアイテム全体のコンテナ
      const itemDiv = document.createElement("div");
      itemDiv.className = "accordion-item";

      // クリック可能なヘッダー部分
      const headerBtn = document.createElement("button");
      headerBtn.className = "accordion-header";
      headerBtn.type = "button";
      headerBtn.dataset.examKey = examKey;
      headerBtn.id = `${uniqueId}-header`;
      // アクセシビリティ(ARIA)属性を設定し、開閉状態と関連パネルを示す
      headerBtn.setAttribute("aria-expanded", "false");
      headerBtn.setAttribute("aria-controls", `${uniqueId}-panel`);
      // 試験データに定義された色をスタイルに適用
      if (exam.header_bg_color)
        headerBtn.style.backgroundColor = exam.header_bg_color;
      if (exam.header_text_color)
        headerBtn.style.color = exam.header_text_color;

      // ヘッダー内のアイコン
      const icon = document.createElement("span");
      icon.className = "material-icons accordion-icon";
      icon.textContent = exam.icon_name || "expand_more"; // アイコンがなければデフォルト
      if (exam.header_text_color) icon.style.color = exam.header_text_color;

      // ヘッダー内のタイトル
      const title = document.createElement("span");
      title.className = "accordion-title";
      title.textContent = `${exam.vendor || ""} - ${
        exam.exam_name || "不明な試験"
      }`;

      // ヘッダー内のメタ情報（平均正答率など）
      const meta = document.createElement("span");
      meta.className = "accordion-meta";
      meta.textContent = `平均正答率: ${exam.averageAccuracy || "N/A"}`;
      if (exam.header_text_color) meta.style.color = exam.header_text_color;

      // ヘッダーに各要素を追加
      headerBtn.appendChild(icon);
      headerBtn.appendChild(title);
      headerBtn.appendChild(meta);

      // 開閉するパネル部分
      const panelDiv = document.createElement("div");
      panelDiv.className = "accordion-panel";
      panelDiv.id = `${uniqueId}-panel`;
      panelDiv.setAttribute("role", "region");
      panelDiv.setAttribute("aria-labelledby", `${uniqueId}-header`);

      // 試験の説明文があれば表示
      if (exam.description) {
        const desc = document.createElement("p");
        desc.className = "accordion-description";
        desc.textContent = exam.description;
        panelDiv.appendChild(desc);
      }

      // --- Area Selection Button ---
      if (exam.availableAreas && exam.availableAreas.length > 0) {
        const areaContainer = document.createElement("div");
        areaContainer.className = "quiz-area-container";
        areaContainer.innerHTML = `<h3>学習する領域を選択してください</h3>`;

        const selectButton = document.createElement("button");
        selectButton.className = "select-areas-btn mui-select-like";
        selectButton.dataset.examKey = examKey;
        selectButton.innerHTML = `<span class="select-areas-text">すべての領域</span><i class="material-icons">arrow_drop_down</i>`;

        areaContainer.appendChild(selectButton);
        panelDiv.appendChild(areaContainer);
      }

      // 「演習開始」ボタン
      const startButton = document.createElement("button");
      startButton.className = "mui-button start-quiz-btn";
      startButton.innerHTML =
        '<span class="material-icons button-icon">play_arrow</span> 演習開始';
      startButton.dataset.examKey = examKey;

      // 「学習履歴をリセット」ボタン
      const resetButton = document.createElement("button");
      resetButton.className = "mui-button secondary danger reset-history-btn";
      resetButton.innerHTML =
        '<span class="material-icons button-icon">delete_sweep</span> 学習履歴をリセット';
      resetButton.dataset.examKey = examKey;

      // パネルにボタンを追加
      panelDiv.appendChild(startButton);
      panelDiv.appendChild(resetButton);
      // アイテム全体にヘッダーとパネルを追加
      itemDiv.appendChild(headerBtn);
      itemDiv.appendChild(panelDiv);
      // DocumentFragmentにアイテムを追加
      fragment.appendChild(itemDiv);
    });

    // すべてのアイテムが追加されたDocumentFragmentを、一度のDOM操作でコンテナに追加
    container.appendChild(fragment);
  },

  /**
   * 指定されたアコーディオンヘッダーに対応するパネルを開閉します。
   * @param {HTMLElement} header - クリックされたアコーディオンヘッダー要素。
   */
  toggleAccordionPanel(header: HTMLElement) {
    if (!header) return; // ヘッダー要素がなければ何もしない

    const panel = header.nextElementSibling as HTMLElement; // ヘッダーの次の要素がパネル
    // 現在クリックされたヘッダーが既にアクティブ（開いている）状態か判定
    const isActive = header.classList.contains(
      Constants.CSS_CLASSES.ACCORDION_HEADER_ACTIVE
    );

    // 他に開いているアコーディオンパネルがあり、それが現在のパネルと異なる場合、それを閉じる
    if (State.activeAccordionPanel && State.activeAccordionPanel !== panel) {
      const activeHeader = State.activeAccordionPanel
        .previousElementSibling as HTMLElement;
      if (activeHeader) this.closeAccordionPanel(activeHeader);
    }

    // パネルが現在閉じていれば、開く処理を実行
    if (!isActive) {
      header.classList.add(Constants.CSS_CLASSES.ACCORDION_HEADER_ACTIVE);
      header.setAttribute("aria-expanded", "true"); // ARIA属性で開いていることを示す
      panel.classList.add(Constants.CSS_CLASSES.ACCORDION_PANEL_ACTIVE);
      // スムーズな開閉アニメーションのため、maxHeightにパネルの実際の高さを設定
      panel.style.maxHeight = `${panel.scrollHeight + 30}px`; // スクロール高さに少し余裕を持たせる
      const icon = header.querySelector(".accordion-icon");
      if (icon) icon.textContent = "expand_less"; // アイコンを「閉じる」状態に変更
      State.activeAccordionPanel = panel; // 現在開いているパネルをStateに記録
    } else {
      // パネルが既に開いていれば、閉じる処理を実行
      this.closeAccordionPanel(header);
    }
  },

  /**
   * 指定されたヘッダーに対応するアコーディオンパネルを閉じます。
   * @param {HTMLElement} header - 閉じるアコーディオンパネルのヘッダー要素。
   */
  closeAccordionPanel(header: HTMLElement) {
    if (!header) return; // ヘッダー要素がなければ何もしない
    const panel = header.nextElementSibling as HTMLElement; // ヘッダーの次の要素がパネル

    // パネルを閉じるためのクラスとARIA属性を削除・リセット
    header.classList.remove(Constants.CSS_CLASSES.ACCORDION_HEADER_ACTIVE);
    header.setAttribute("aria-expanded", "false"); // ARIA属性で閉じていることを示す
    panel.classList.remove(Constants.CSS_CLASSES.ACCORDION_PANEL_ACTIVE);
    panel.style.maxHeight = ""; // maxHeightをリセットしてパネルを完全に閉じる

    // アイコンを元の状態（展開アイコン）に戻す
    const icon = header.querySelector(".accordion-icon");
    const examData = this._examDataCache[header.dataset.examKey as string]; // キャッシュから試験データを取得
    if (icon && examData) {
      icon.textContent = examData.icon_name || "expand_more";
    }

    // Stateに記録されているアクティブパネルが現在のパネルであれば、それをリセット
    if (State.activeAccordionPanel === panel) {
      State.activeAccordionPanel = null;
      State.selectedAreaIds = []; // アコーディオンを閉じたら選択領域をリセット
    }
  },

  /**
   * クイズの進行状況に応じてプログレスバーの表示を更新します。
   */
  updateProgressBar() {
    const total = State.currentQuestions.length; // 総問題数
    const current = State.currentQuestionIndex + 1; // 現在の問題番号 (1-indexed)
    // プログレスバーの進捗率を計算 (問題解答前の状態)
    const percentage =
      total > 0 ? (State.currentQuestionIndex / total) * 100 : 0; // 0問の場合は0%

    // 下部と上部の両方のプログレスバーを更新
    const progressBars = [
      this.elements.bottomProgressBar,
      this.elements.topProgressBar,
    ];
    progressBars.forEach((bar) => {
      if (bar) {
        bar.style.width = `${percentage}%`; // 幅を更新
        // アクセシビリティのためにARIA属性も更新
        bar.setAttribute("aria-valuenow", current.toString());
        bar.setAttribute("aria-valuemin", "1");
        bar.setAttribute("aria-valuemax", total.toString());
      }
    });

    // プログレステキストを更新
    if (this.elements.progressText) {
      this.elements.progressText.textContent = `問題 ${current} / ${total}`;
    }
  },

  /**
   * 新しい問題を表示する前に、クイズUIの状態をリセットします。
   */
  resetQuizUIForNewQuestion() {
    // 新しい問題が画面上部に表示されるようにスクロール
    window.scrollTo(0, 0);
    // 前の問題の解答フィードバックを非表示にする
    this.toggleElementVisibility(this.elements.feedback, false);
    if (this.elements.feedback) {
      // フィードバックコンテナのスタイルクラスをリセットし、アニメーションクラスを削除
      this.elements.feedback.className = "feedback-container hidden";
      this.elements.feedback.classList.remove(
        Constants.CSS_CLASSES.FEEDBACK_POP_IN
      );
    }

    // 問題文、進捗テキスト、問題情報エリアなどのアニメーションクラスと遅延をリセット
    const animatedElements = [
      "questionText",
      "progressText",
      "questionInfoArea",
      "questionInfoDifficulty",
      "questionInfoImportance",
      "questionInfoTagsContainer",
    ];
    animatedElements.forEach((key) => {
      const element = this.elements[key as keyof UIElements];
      if (element) {
        element.classList.remove(Constants.CSS_CLASSES.QUESTION_ELEMENT_APPEAR);
        element.style.animationDelay = ""; // アニメーション遅延をクリア
      }
    });

    // 全体解説コンテナを非表示にする
    this.toggleElementVisibility(
      this.elements.overallExplanationContainer,
      false
    );
    this.elements.optionsList.innerHTML = ""; // 選択肢リストをクリア
    this.toggleElementVisibility(this.elements.optionsList, true); // 選択肢リストを表示状態にリセット
    // 「次の問題へ」ボタンと「結果を見る」ボタンを非表示にする
    this.toggleElementVisibility(this.elements.nextQuestionBtn, false);
    this.toggleElementVisibility(this.elements.showResultBtn, false);
    this.toggleElementVisibility(this.elements.editQuestionMenuItem, false);
    (this.elements.answerBtn as HTMLButtonElement).disabled = false; // 解答ボタンを有効にする
    this.toggleElementVisibility(this.elements.answerBtn, true); // 解答ボタンを表示する
  },

  /**
   * 現在の問題を画面に表示します。問題文、選択肢、メタ情報などを設定します。
   * @param {string} finalDisplayMode - クイズの表示モード（例: 'flashcard'）。
   */
  displayQuestion(finalDisplayMode: string) {
    // UIを新しい問題表示用にリセット
    this.resetQuizUIForNewQuestion();

    // Stateから現在の問題データを取得
    const question = State.getCurrentQuestion();
    // もし問題がなければ（全問解き終わったら）、結果表示画面に遷移
    if (!question) {
      App.displayResults();
      return;
    }

    // 問題文と進捗テキストにフェードインアニメーションを適用
    ["questionText", "progressText"].forEach((key, idx) => {
      const element = this.elements[key as keyof UIElements];
      if (element) {
        element.classList.add(Constants.CSS_CLASSES.QUESTION_ELEMENT_APPEAR);
        element.style.animationDelay = `${idx * 0.1}s`; // わずかな遅延で順次表示
        // アニメーション終了後にクラスを削除
        element.addEventListener(
          "animationend",
          (e: Event) =>
            (e.target as HTMLElement).classList.remove(
              Constants.CSS_CLASSES.QUESTION_ELEMENT_APPEAR
            ),
          { once: true }
        );
      }
    });

    // 問題文を表示、なければデフォルトメッセージ
    this.elements.questionText.textContent =
      question.questionText || "問題文がありません";

    this.updateProgressBar(); // プログレスバーを更新
    this.displayQuestionInfo(question); // 問題のメタ情報を表示

    // 選択肢のUIを生成するためにDocumentFragmentを使用（DOM操作のパフォーマンス向上）
    const fragment = document.createDocumentFragment();
    // 選択肢をシャッフルしてStateに保存し、ユーザーに毎回異なる順序で提示
    State.shuffledOptionDetails = State.shuffleArray([
      ...(question.options || []),
    ]);

    // シャッフルされた各選択肢に対してUI要素を生成
    State.shuffledOptionDetails.forEach(
      (optionDetail: Option, index: number) => {
        const optionItem = document.createElement("div");
        optionItem.className = `option-item ${Constants.CSS_CLASSES.OPTION_ITEM_APPEAR}`;
        optionItem.style.animationDelay = `${0.2 + index * 0.08}s`; // 選択肢ごとに遅延アニメーション
        optionItem.addEventListener(
          "animationend",
          (e) =>
            (e.target as HTMLElement).classList.remove(
              Constants.CSS_CLASSES.OPTION_ITEM_APPEAR
            ),
          { once: true }
        );

        const radio = document.createElement("input");
        radio.type = "radio";
        radio.name = "quiz_option"; // 同じname属性でグループ化
        radio.id = `option_${index}`;
        radio.value = optionDetail.text || "";
        radio.dataset.optionId = optionDetail.id.toString(); // オプションIDをデータ属性に保存

        const label = document.createElement("label");
        label.htmlFor = `option_${index}`;

        const textContainer = document.createElement("div");
        textContainer.className = "option-text-container";
        const prefix = document.createElement("span");
        prefix.className = "option-prefix";
        prefix.textContent = `${
          Constants.OPTION_PREFIXES[index % Constants.OPTION_PREFIXES.length]
        }. `; // A, B, C...のプレフィックス
        const text = document.createElement("span");
        text.className = "option-text";
        text.textContent = optionDetail.text || "";
        textContainer.appendChild(prefix);
        textContainer.appendChild(text);
        label.appendChild(textContainer);

        // 選択肢固有の解説があれば、非表示の状態で追加
        if (optionDetail.explanation) {
          const explanation = document.createElement("div");
          explanation.className = `${Constants.CSS_CLASSES.OPTION_SPECIFIC_EXPLANATION} ${Constants.CSS_CLASSES.HIDDEN}`;
          explanation.textContent = optionDetail.explanation;
          label.appendChild(explanation);
        }

        optionItem.appendChild(radio);
        optionItem.appendChild(label);
        fragment.appendChild(optionItem);
      }
    );
    this.elements.optionsList.appendChild(fragment);

    // フラッシュカードモードの処理
    if (finalDisplayMode === "flashcard") {
      const options =
        this.elements.optionsList.querySelectorAll<HTMLElement>(".option-item");
      options.forEach((opt) => opt.classList.add("concealed")); // 最初は選択肢を隠す
      const showOptions = () => {
        options.forEach((opt) => opt.classList.remove("concealed")); // クリックで表示
        State.recallPhaseEndTime = Date.now(); // 回想フェーズ終了時刻を記録
      };
      this.elements.quizArea.addEventListener("click", showOptions, {
        once: true,
      }); // クイズエリアのクリックで選択肢を表示
    }

    State.questionStartTime = Date.now(); // 問題開始時刻を記録
  },

  /**
   * 問題のメタ情報（領域、難易度、重要度、タグ）をUIに表示します。
   * @param {Question} question - 表示する問題データオブジェクト。
   */
  displayQuestionInfo(question: Question) {
    if (!question) return; // 問題データがなければ何もしない

    // 領域情報を表示
    this.elements.questionInfoArea.innerHTML = `<span class="material-icons">label_outline</span> 領域: <strong>${
      question.area_name || "未分類"
    }</strong>`;

    // 難易度情報を表示（1-10の範囲外は"N/A"）
    const difficulty =
      question.difficulty &&
      question.difficulty > 0 &&
      question.difficulty <= 10
        ? `${question.difficulty}/10`
        : "N/A";
    this.elements.questionInfoDifficulty.innerHTML = `<span class="material-icons">layers</span> 難易度: <strong>${difficulty}</strong>`;

    // 重要度情報を表示（1-10の範囲外は"N/A"）
    const importance =
      question.importance &&
      question.importance > 0 &&
      question.importance <= 10
        ? `${question.importance}/10`
        : "N/A";
    this.elements.questionInfoImportance.innerHTML = `<span class="material-icons">priority_high</span> 重要度: <strong>${importance}</strong>`;

    const tagsContainer = this.elements.questionInfoTagsContainer;
    tagsContainer.innerHTML = ""; // タグコンテナをクリア
    // タグがあれば、各タグをspan要素として追加
    if (question.tags && question.tags.length > 0) {
      question.tags.forEach((tag: string) => {
        const tagElement = document.createElement("span");
        tagElement.className = "tag-item";
        tagElement.textContent = tag;
        tagsContainer.appendChild(tagElement);
      });
    }
  },

  /**
   * ユーザーの解答に対するフィードバック（正解/不正解、解説）をUIに表示します。
   * @param {boolean} isCorrect - ユーザーの解答が正解だったかどうかのフラグ。
   */
  displayAnswerFeedback(isCorrect: boolean) {
    const question = State.getCurrentQuestion(); // 現在の問題データを取得
    if (!question) return; // 問題データがなければ何もしない

    // ユーザーが選択したラジオボタンとテキストを取得
    const selectedRadio =
      this.elements.optionsList.querySelector<HTMLInputElement>(
        'input[name="quiz_option"]:checked'
      );
    const selectedText = selectedRadio ? selectedRadio.value : null;

    // すべての選択肢をループし、正誤に応じたスタイルを適用
    this.elements.optionsList
      .querySelectorAll<HTMLElement>(".option-item")
      .forEach((item) => {
        // 以前の判定クラスを一旦クリア (再描画対応)
        item.classList.remove(
          Constants.CSS_CLASSES.CORRECT_ANSWER_ITEM,
          Constants.CSS_CLASSES.SELECTED_INCORRECT_ITEM,
          Constants.CSS_CLASSES.DISABLED_OPTION
        );

        item.classList.add(Constants.CSS_CLASSES.DISABLED_OPTION); // すべての選択肢を無効化
        const radio = item.querySelector<HTMLInputElement>(
          'input[type="radio"]'
        );
        if (radio) {
          radio.disabled = true; // ラジオボタンを無効化
          // 正解の選択肢にスタイルを適用
          if (radio.value === question.correctAnswer) {
            item.classList.add(Constants.CSS_CLASSES.CORRECT_ANSWER_ITEM);
          }
          // ユーザーが選択した不正解の選択肢にスタイルを適用
          else if (radio.value === selectedText && !isCorrect) {
            item.classList.add(Constants.CSS_CLASSES.SELECTED_INCORRECT_ITEM);
          }
        }
        // 選択肢固有の解説があれば表示する
        const explanation = item.querySelector<HTMLElement>(
          `.${Constants.CSS_CLASSES.OPTION_SPECIFIC_EXPLANATION}`
        );
        if (explanation) this.toggleElementVisibility(explanation, true);
      });

    // 全体解説があれば表示
    if (question.overallExplanation) {
      this.elements.overallExplanation.textContent =
        question.overallExplanation;
      this.toggleElementVisibility(
        this.elements.overallExplanationContainer,
        true
      );
    }

    // シャッフルされた選択肢の中から正解の選択肢を見つけ、そのプレフィックス（A, Bなど）を取得
    const correctOptionDetail = State.shuffledOptionDetails.find(
      (opt) => opt.text === question.correctAnswer
    );
    const correctPrefix = correctOptionDetail
      ? Constants.OPTION_PREFIXES[
          State.shuffledOptionDetails.indexOf(correctOptionDetail)
        ]
      : "";

    // 正解/不正解に応じたフィードバックメッセージとアイコンを表示
    this.elements.feedback.classList.remove(
      Constants.CSS_CLASSES.CORRECT_FEEDBACK,
      Constants.CSS_CLASSES.INCORRECT_FEEDBACK
    );

    if (isCorrect) {
      this.elements.feedbackIcon.textContent = "check_circle";
      this.elements.feedbackMessage.textContent = "正解！";
      this.elements.feedback.classList.add(
        Constants.CSS_CLASSES.CORRECT_FEEDBACK
      );
    } else {
      this.elements.feedbackIcon.textContent = "error_outline";
      this.elements.feedbackMessage.textContent = `不正解。正解は: ${correctPrefix}. ${question.correctAnswer}`;
      this.elements.feedback.classList.add(
        Constants.CSS_CLASSES.INCORRECT_FEEDBACK
      );
    }
    this.toggleElementVisibility(this.elements.feedback, true); // フィードバックコンテナを表示
    // フィードバックにポップインアニメーションを適用
    this.elements.feedback.classList.add(Constants.CSS_CLASSES.FEEDBACK_POP_IN);
    this.elements.feedback.addEventListener(
      "animationend",
      (e) =>
        (e.target as HTMLElement).classList.remove(
          Constants.CSS_CLASSES.FEEDBACK_POP_IN
        ),
      { once: true }
    );

    // プログレスバーを最終的な進捗率で更新
    const finalProgress =
      ((State.currentQuestionIndex + 1) / State.currentQuestions.length) * 100;
    [this.elements.bottomProgressBar, this.elements.topProgressBar].forEach(
      (bar) => {
        if (bar) {
          bar.style.width = `${finalProgress}%`;
          bar.setAttribute(
            "aria-valuenow",
            (State.currentQuestionIndex + 1).toString()
          );
        }
      }
    );

    // 最後の問題であれば「結果を見る」ボタン、そうでなければ「次の問題へ」ボタンを表示
    if (State.isLastQuestion()) {
      this.toggleElementVisibility(this.elements.showResultBtn, true);
    } else {
      this.toggleElementVisibility(this.elements.nextQuestionBtn, true);
    }

    // 編集権限がある場合のみ、編集メニュー項目を表示
    if (State.hasEditPermission) {
      this.toggleElementVisibility(this.elements.editQuestionMenuItem, true);
    } else {
      this.toggleElementVisibility(this.elements.editQuestionMenuItem, false);
    }

    // 正解の選択肢、またはフィードバックメッセージまでスクロール
    const correctElement = this.elements.optionsList.querySelector(
      `.${Constants.CSS_CLASSES.CORRECT_ANSWER_ITEM}`
    );
    const targetElement = correctElement || this.elements.feedback;
    targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
  },

  /**
   * クイズ結果をチャート（ドーナツ、レーダー）で描画します。
   */
  renderResultCharts() {
    // --- 総合結果ドーナツチャートの描画 ---
    const resultChartCtx = (
      this.elements.resultChart as HTMLCanvasElement
    )?.getContext("2d");
    if (resultChartCtx) {
      // 既存のチャートインスタンスがあれば破棄して再描画に備える
      if (State.resultChartInstance) State.resultChartInstance.destroy();

      // アクセシビリティのためのARIA属性を設定
      this.elements.resultChart.setAttribute("role", "img");
      this.elements.resultChart.setAttribute(
        "aria-label",
        `総合結果：正解 ${State.score} 問、不正解 ${
          State.currentQuestions.length - State.score
        } 問のドーナツチャート`
      );

      // 新しいドーナツチャートインスタンスを作成
      State.resultChartInstance = new Chart(resultChartCtx, {
        type: "doughnut",
        data: {
          labels: ["正解", "不正解"],
          datasets: [
            {
              data: [
                State.score, // 正解数
                State.currentQuestions.length - State.score, // 不正解数
              ],
              backgroundColor: [
                "rgba(75, 192, 192, 0.7)", // 正解の背景色
                "rgba(255, 99, 132, 0.7)", // 不正解の背景色
              ],
              borderColor: ["rgba(75, 192, 192, 1)", "rgba(255, 99, 132, 1)"], // 枠線の色
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true, // レスポンシブ対応
          maintainAspectRatio: false, // アスペクト比を維持しない
          plugins: {
            legend: { position: "bottom" }, // 凡例を下に配置
            title: { display: true, text: "総合結果" }, // タイトルを表示
          },
        },
      });
    }

    // --- 領域別正解率レーダーチャートの描画 ---
    const areaChartCtx = (
      this.elements.areaResultChart as HTMLCanvasElement
    )?.getContext("2d");
    // 領域別統計データが存在する場合のみチャートを描画
    if (areaChartCtx && Object.keys(State.areaStats).length > 0) {
      // 既存のチャートインスタンスがあれば破棄
      if (State.areaResultChartInstance)
        State.areaResultChartInstance.destroy();

      // アクセシビリティのためのARIA属性を設定
      this.elements.areaResultChart.setAttribute("role", "img");
      this.elements.areaResultChart.setAttribute(
        "aria-label",
        `領域別の正解率を示すレーダーチャート`
      );

      const areaIdNameMap = new Map(
        State.currentExamData?.availableAreas.map((area) => [
          area.id,
          area.name,
        ])
      );

      const labels = Object.keys(State.areaStats).map((areaIdStr) => {
        const areaId = parseInt(areaIdStr, 10);
        // First, try to get the name from the pre-fetched exam data
        const areaName = areaIdNameMap.get(areaId);
        if (areaName) {
          return areaName;
        }

        // If not found, fallback to searching in the current questions list
        const questionForThisArea = State.currentQuestions.find(
          (q) => q.area_id === areaId
        );
        if (questionForThisArea && questionForThisArea.area_name) {
          return questionForThisArea.area_name;
        }

        // If still not found, return a default value
        return "不明な領域";
      });

      // 各領域の実際の正解率を計算
      const actualData = Object.values(State.areaStats).map((stats) => {
        return stats.total > 0
          ? parseFloat(((stats.correct / stats.total) * 100).toFixed(1))
          : 0;
      });
      // 合格ライン（目標ライン）を設定。試験データにあればそれを使用、なければデフォルト70%
      const passingScore = State.currentExamData?.passing_score_percent ?? 70;
      // 目標ラインのデータを生成（すべての領域で同じ合格ライン）
      const targetData = new Array(labels.length).fill(passingScore);

      // 領域チャートコンテナを表示
      this.toggleElementVisibility(this.elements.areaChartContainer, true);
      // 新しいレーダーチャートインスタンスを作成
      State.areaResultChartInstance = new Chart(areaChartCtx, {
        type: "radar",
        data: {
          labels,
          datasets: [
            {
              label: "あなたの正解率 (%)",
              data: actualData,
              fill: true,
              backgroundColor: "rgba(54, 162, 235, 0.2)",
              borderColor: "rgb(54, 162, 235)",
              pointBackgroundColor: "rgb(54, 162, 235)",
              pointBorderColor: "#fff",
              pointHoverBackgroundColor: "#fff",
              pointHoverBorderColor: "rgb(54, 162, 235)",
            },
            {
              label: `目標ライン (${passingScore}%)`,
              data: targetData,
              fill: false,
              borderColor: "rgba(255, 99, 132, 0.7)",
              borderDash: [5, 5], // 破線で表示
              pointRadius: 0, // ポイントを表示しない
              pointHitRadius: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          elements: { line: { borderWidth: 2 } }, // 線の太さ
          scales: {
            r: {
              angleLines: { display: true },
              suggestedMin: 0,
              suggestedMax: 100,
              ticks: {
                stepSize: 20, // 目盛りの間隔
                callback: (value: string | number) => `${value}%`, // 目盛りの表示形式
              },
              pointLabels: {
                font: { size: 13 },
                callback: function (label: string) {
                  const maxLineLength = 10; // 1行の最大文字数
                  if (label.length <= maxLineLength) {
                    return label;
                  }

                  let currentLine = "";
                  const lines = [];
                  for (let i = 0; i < label.length; i++) {
                    currentLine += label[i];
                    if (currentLine.length >= maxLineLength) {
                      lines.push(currentLine);
                      currentLine = "";
                    }
                  }
                  if (currentLine.length > 0) {
                    lines.push(currentLine);
                  }
                  return lines;
                },
              },
            },
          },
          plugins: {
            legend: { position: "top" }, // 凡例を上に配置
            title: { display: true, text: "領域別 正解率" }, // タイトルを表示
            tooltip: {
              callbacks: {
                label: (context: TooltipItem<"radar">) =>
                  `${context.dataset.label || ""}: ${context.formattedValue}%`, // ツールチップのラベル表示形式をカスタマイズ
              },
            },
          },
        },
      });
    } else if (this.elements.areaChartContainer) {
      // 領域別統計データがない場合はチャートコンテナを非表示にする
      this.toggleElementVisibility(this.elements.areaChartContainer, false);
    }
  },

  /**
   * ログインしているユーザーのメールアドレスをUIに表示します。
   * @param {string} email - 表示するユーザーのメールアドレス。
   */
  updateUserInfo(email: string) {
    // メニュー内のユーザーメールアドレス表示を更新
    this.elements.menuUserEmail.textContent = email;
  },

  /**
   * 問題編集モーダルを表示します。
   * @param {QuestionEditData} data - 編集対象の問題データ。
   * @param {(updatedData: UpdateQuestionRequest) => void} onSave - 保存ボタンクリック時のコールバック。
   * @param {() => void} onCancel - キャンセルボタンクリック時のコールバック。
   */
  showEditQuestionModal(data, onSave, onCancel) {
    this.elements.customConfirmTitle.textContent = "問題の編集";
    // 幅を広げるクラスを追加
    if (this.elements.customDialogContent) {
      this.elements.customDialogContent.classList.add("wide");
    }
    const dialogBody = this.elements.customDialogBody;
    dialogBody.innerHTML = ""; // クリア

    const form = document.createElement("div");
    form.className = "edit-question-form";

    // --- セクション作成ヘルパー ---
    const createAccordionSection = (
      title: string,
      content: HTMLElement,
      isExpanded: boolean = false
    ) => {
      const section = document.createElement("div");
      section.className = "edit-section-accordion";

      const header = document.createElement("div");
      header.className = `edit-section-header ${isExpanded ? "expanded" : ""}`;
      header.innerHTML = `<span>${title}</span><span class="material-icons">${
        isExpanded ? "expand_less" : "expand_more"
      }</span>`;

      const panel = document.createElement("div");
      panel.className = `edit-section-panel ${isExpanded ? "expanded" : ""}`;
      panel.appendChild(content);

      header.addEventListener("click", () => {
        const expanded = header.classList.toggle("expanded");
        panel.classList.toggle("expanded", expanded);
        header.querySelector(".material-icons")!.textContent = expanded
          ? "expand_less"
          : "expand_more";
      });

      section.appendChild(header);
      section.appendChild(panel);
      return section;
    };

    // --- メタデータセクション ---
    const mContent = document.createElement("div");

    const areaOptions = data.availableAreas
      .map(
        (a) =>
          `<option value="${a.id}" ${
            a.id === data.areaId ? "selected" : ""
          }>${a.name} (${a.questionCount}問)</option>`
      )
      .join("");

    mContent.innerHTML = `
      <label class="edit-label">試験領域</label>
      <select id="edit-q-area" class="edit-input">
        <option value="">-- 領域を選択 --</option>
        ${areaOptions}
      </select>
      <div class="edit-meta-row">
        <div class="edit-meta-item">
          <label class="edit-label">難易度 (1-10)</label>
          <input type="number" id="edit-q-difficulty" min="1" max="10" value="${
            data.difficulty || 1
          }" class="edit-input">
        </div>
        <div class="edit-meta-item">
          <label class="edit-label">重要度 (1-10)</label>
          <input type="number" id="edit-q-importance" min="1" max="10" value="${
            data.importance || 1
          }" class="edit-input">
        </div>
      </div>
      <label class="edit-label">タグ (カンマ区切り)</label>
      <input type="text" id="edit-q-tags" value="${data.tags.join(
        ", "
      )}" class="edit-input">
    `;
    form.appendChild(createAccordionSection("メタデータ", mContent, false));

    // --- 問題文と解説セクション ---
    const qContent = document.createElement("div");
    qContent.innerHTML = `
      <label class="edit-label">問題文</label>
      <textarea id="edit-q-text" class="edit-textarea">${data.questionText}</textarea>
      <label class="edit-label">全体解説</label>
      <textarea id="edit-q-explanation" class="edit-textarea">${data.overallExplanation}</textarea>
    `;
    form.appendChild(createAccordionSection("問題文と解説", qContent, true));

    // --- 選択肢セクション ---
    const oContent = document.createElement("div");
    oContent.className = "edit-options-list";
    data.options.forEach((opt, idx) => {
      const optItem = document.createElement("div");
      optItem.className = "edit-option-item";
      optItem.innerHTML = `
        <div class="edit-option-header">
          <strong>選択肢 ${String.fromCharCode(65 + idx)}</strong>
          <label><input type="checkbox" class="edit-opt-correct" ${
            opt.isCorrect ? "checked" : ""
          }> 正解</label>
        </div>
        <textarea class="edit-opt-text edit-textarea" placeholder="選択肢テキスト">${
          opt.text
        }</textarea>
        <textarea class="edit-opt-explanation edit-textarea" placeholder="選択肢解説（任意）">${
          opt.explanation || ""
        }</textarea>
      `;
      optItem.dataset.id = opt.id.toString();

      // 正解チェックボックスの排他制御
      const checkbox = optItem.querySelector(
        ".edit-opt-correct"
      ) as HTMLInputElement;
      checkbox.addEventListener("change", () => {
        if (checkbox.checked) {
          // 他のすべてのチェックボックスを外す
          const allCheckboxes = oContent.querySelectorAll(".edit-opt-correct");
          allCheckboxes.forEach((cb) => {
            if (cb !== checkbox) (cb as HTMLInputElement).checked = false;
          });
        }
      });

      oContent.appendChild(optItem);
    });
    form.appendChild(createAccordionSection("選択肢", oContent, true));

    // エラーメッセージ用コンテナを追加
    const errorContainer = document.createElement("div");
    errorContainer.id = "edit-error-container";
    errorContainer.className = "edit-error-message hidden";
    // タイトルとフォームの間に挿入したいが、dialogBodyにはタイトルがない（タイトルはcustomConfirmTitleにある）
    // フォームの先頭に挿入するのが適切
    form.insertBefore(errorContainer, form.firstChild);

    dialogBody.appendChild(form);

    this.elements.customConfirmOkBtn.textContent = "保存";
    this.elements.customConfirmCancelBtn.textContent = "キャンセル";
    this.toggleElementVisibility(this.elements.customConfirmCancelBtn, true);

    State._currentDialogOkCallback = () => {
      // 保存処理前にエラーをクリア

      this.clearEditError();

      // エリア情報の取得

      const areaSelect = document.getElementById(
        "edit-q-area"
      ) as unknown as HTMLSelectElement;

      const areaId = areaSelect.value ? parseInt(areaSelect.value, 10) : null;

      const selectedOptionText =
        areaSelect.options[areaSelect.selectedIndex]?.text || "";

      const areaName = selectedOptionText.replace(/\s\(\d+問\)$/, "");

      const updatedData: UpdateQuestionRequest = {
        questionText: (
          document.getElementById("edit-q-text") as HTMLTextAreaElement
        ).value,

        overallExplanation: (
          document.getElementById("edit-q-explanation") as HTMLTextAreaElement
        ).value,

        areaId: areaId,

        areaName: areaName || null,

        difficulty:
          parseInt(
            (document.getElementById("edit-q-difficulty") as HTMLInputElement)
              .value,

            10
          ) || 1,

        importance:
          parseInt(
            (document.getElementById("edit-q-importance") as HTMLInputElement)
              .value,

            10
          ) || 1,

        tags: (document.getElementById("edit-q-tags") as HTMLInputElement).value

          .split(",")

          .map((t) => t.trim())

          .filter((t) => t !== ""),

        options: Array.from(document.querySelectorAll(".edit-option-item")).map(
          (el) => {
            const item = el as HTMLElement;
            return {
              id: parseInt(item.dataset.id || "0", 10),
              text: (
                item.querySelector(".edit-opt-text") as HTMLTextAreaElement
              ).value,
              explanation:
                (
                  item.querySelector(
                    ".edit-opt-explanation"
                  ) as HTMLTextAreaElement
                ).value || null,
              isCorrect: (
                item.querySelector(".edit-opt-correct") as HTMLInputElement
              ).checked,
            };
          }
        ),
      };
      onSave(updatedData);
    };
    State._currentDialogCancelCallback = onCancel;

    this.toggleElementVisibility(this.elements.customConfirmDialog, true);
  },

  /**
   * 編集モーダル内にエラーメッセージを表示します。
   * @param {string} message - エラーメッセージ。
   */
  showEditError(message: string) {
    const container = document.getElementById("edit-error-container");
    if (container) {
      container.textContent = message;
      container.classList.remove(Constants.CSS_CLASSES.HIDDEN);
      // エラーが見えるようにスクロール
      container.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  },

  /**
   * 編集モーダル内のエラーメッセージをクリアします。
   */
  clearEditError() {
    const container = document.getElementById("edit-error-container");
    if (container) {
      container.textContent = "";
      container.classList.add(Constants.CSS_CLASSES.HIDDEN);
    }
  },
};
